/* eslint-disable no-param-reassign */
import Button from '@/atoms/Button';
import { ErrorLabel, Label } from '@/atoms/forms/labels';
import clx from 'classnames';
import { InputHTMLAttributes } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { SIZE } from 'types/utils';
import HTMLInput from '../Input/Text/HTMLInput';

export type CounterTypeProps = Omit<
  InputHTMLAttributes<InputHTMLAttributes<HTMLInputElement>>,
  'onChange' | 'min' | 'max'
> & {
  label?: string;
  name: string;
  hintText?: string;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
};

const CounterInput = ({
  label,
  name,
  disabled,
  min = 0,
  max = 99,
  required,
  hintText,
  className,
  onChange: onChangeProps,
}: CounterTypeProps) => {
  const { control, formState } = useFormContext();
  const { field } = useController({
    name,
    control,
    defaultValue: 0,
  });
  const { errors } = formState;
  const error = errors[name];

  const onChange = (value: number) => {
    if (value > max) {
      value = max;
    } else if (value < min) {
      value = min;
    }

    field.onChange(value);
    if (onChangeProps) {
      onChangeProps(value);
    }
  };

  const increase = () => {
    onChange(Number(field.value) + 1);
  };
  const decrease = () => {
    onChange(Number(field.value) - 1);
  };

  return (
    <>
      {label && <Label className="px-2" disabled={disabled} label={label} name={name} required={required} />}
      <div className={clx('flex gap-2 justify-center', className)}>
        <Button
          className="flex-1"
          disabled={disabled || min === field.value}
          icon="fa-solid fa-minus"
          onClick={() => decrease()}
          size={SIZE.MD}
        />
        <HTMLInput
          disabled={disabled}
          {...field}
          className={clx('text-center flex-1', max > 99 ? 'min-w-16' : 'min-w-12')}
          id={name}
          max={max}
          min={min}
          onChange={({ target: { value } }) => onChange(Number(value))}
          type="number"
          value={field.value || 0}
        />
        <Button
          className="flex-1"
          disabled={disabled || max === field.value}
          icon="fa-solid fa-plus-large"
          onClick={() => increase()}
          size={SIZE.MD}
        />
      </div>
      {!!hintText && <p className="md-regular">{hintText}</p>}
      {!!error && <ErrorLabel error={error.message} />}
    </>
  );
};

export default CounterInput;
